import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const DropdownMenu = Menu.Root;
export const DropdownMenuTrigger = Menu.Trigger;
export const DropdownMenuGroup = Menu.Group;
export const DropdownMenuRadioGroup = Menu.RadioGroup;
export const DropdownMenuSub = Menu.Sub;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  align = 'end',
  ...props
}: ComponentProps<typeof Menu.Content>) {
  return (
    <Menu.Portal>
      <Menu.Content
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(
          'z-50 min-w-[11rem] overflow-hidden rounded-xl border glass p-1 text-fg shadow-float',
          'animate-pop-in data-[state=closed]:animate-pop-out',
          className,
        )}
        {...props}
      />
    </Menu.Portal>
  );
}

const itemClass =
  'relative flex min-h-7 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-base text-fg-muted outline-none ' +
  'data-[highlighted]:bg-hover data-[highlighted]:text-fg data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-fg-subtle';

export function DropdownMenuItem({
  className,
  shortcut,
  children,
  ...props
}: ComponentProps<typeof Menu.Item> & { shortcut?: ReactNode }) {
  return (
    <Menu.Item className={cn(itemClass, className)} {...props}>
      {children}
      {shortcut && <span className="ml-auto pl-4 text-xs text-fg-faint">{shortcut}</span>}
    </Menu.Item>
  );
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem className={cn(itemClass, 'pr-7', className)} {...props}>
      {children}
      <Menu.ItemIndicator className="absolute right-2 flex items-center">
        <Check aria-hidden className="!size-3 !text-fg" strokeWidth={2} />
      </Menu.ItemIndicator>
    </Menu.RadioItem>
  );
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof Menu.Label>) {
  return <Menu.Label className={cn('px-2 pt-2 pb-1 micro-label', className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof Menu.Separator>) {
  return <Menu.Separator className={cn('mx-0.5 my-1 h-px bg-border', className)} {...props} />;
}
